import React, { useEffect, useState } from 'react';
import * as submasterService from '../../services/submaster.service';
import SubMasterModal from './SubMasterModal';
import { FaEdit, FaTrash, FaPlus, FaSave } from 'react-icons/fa';

// Types that support related items (many-to-many relationship)
const TYPES_WITH_RELATIONS = ['category', 'product-name'];

// Determine the related type for each submaster type
const getRelatedType = (type) => {
  if (type === 'category') return 'product-name';
  if (type === 'product-name') return 'category';
  return null;
};

// Get the label for related items based on type
const getRelatedLabel = (type) => {
  if (type === 'category') return 'Product Names';
  if (type === 'product-name') return 'Categories';
  return 'Related Items';
};

// Check if this type supports related items
const hasRelatedItems = (type) => TYPES_WITH_RELATIONS.includes(type);

const SubMasterManager = ({ type, title }) => {
  const [items, setItems] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]); // Items from the related type
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);

  const supportsRelations = hasRelatedItems(type);
  const relatedType = supportsRelations ? getRelatedType(type) : null;
  const relatedLabel = supportsRelations ? getRelatedLabel(type) : null;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        // Load items of current type
        const data = await submasterService.getItems(type);
        if (!mounted) return;
        setItems(Array.isArray(data) ? data : data.items || []);

        // Load items of related type for the multi-select (only for supported types)
        if (supportsRelations && relatedType) {
          const relatedData = await submasterService.getItems(relatedType);
          if (!mounted) return;
          const relatedList = Array.isArray(relatedData) ? relatedData : relatedData.items || [];
          // Only show active related items in the selector
          setRelatedItems(relatedList.filter(item => item.active !== false));
        }
      } catch (e) {
        console.error('Load error', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [type, relatedType, supportsRelations]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const payload = { name: name.trim(), active };
    try {
      const created = await submasterService.createItem(type, payload);
      // if backend returns created object in different shape, normalize
      const next = created?.data || created || payload;
      setItems((s) => [next, ...s]);
      setName('');
      setActive(true);
    } catch (e) {
      console.error('Save error', e);
    }
  };

  const handleToggle = async (id, current) => {
    try {
      await submasterService.toggleActive(type, id, !current);
      setItems((s) => s.map((it) => (String(it.id) === String(id) ? { ...it, active: !current } : it)));
    } catch (e) {
      console.error('Toggle error', e);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const openEdit = (item) => {
    setEditing(item);
    setModalOpen(true);
  };

  const handleModalSave = async (payload) => {
    try {
      const savePayload = { 
        name: payload.name, 
        active: payload.active,
        related_ids: payload.related_ids || []
      };

      if (payload.id) {
        const updated = await submasterService.updateItem(type, payload.id, savePayload);
        const updatedItem = updated?.data || updated || payload;
        setItems((s) => s.map((it) => (String(it.id) === String(payload.id) ? { ...it, ...updatedItem } : it)));
      } else {
        const created = await submasterService.createItem(type, savePayload);
        const next = created?.data || created || payload;
        setItems((s) => [next, ...s]);
      }
    } catch (e) {
      console.error('Modal save error', e);
    } finally {
      setModalOpen(false);
      setEditing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await submasterService.deleteItem(type, id);
      setItems((s) => s.filter((it) => String(it.id) !== String(id)));
    } catch (e) {
      console.error('Delete error', e);
    }
  };

  // Helper to render related items as tags
  const renderRelatedTags = (item) => {
    const related = item.related_items || [];
    if (related.length === 0) {
      return <span className="text-muted">-</span>;
    }
    return (
      <div className="d-flex flex-wrap gap-1">
        {related.map((rel) => (
          <span key={rel.id} className="badge bg-secondary" style={{ fontSize: '0.75rem' }}>
            {rel.name}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h3>{title}</h3>

      <form className="row g-2 align-items-center mb-3" onSubmit={handleSave}>
        <div className="col-auto" style={{ minWidth: 300 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} className="form-control" placeholder="Name" />
        </div>
        <div className="col-auto d-flex align-items-center">
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} id="activeSwitch" />
            <label className="form-check-label ms-2" htmlFor="activeSwitch">Active</label>
          </div>
        </div>
        <div className="col-auto">
          <button className="btn btn-primary me-2" type="submit"><FaSave className="me-1"/> Save</button>
          <button type="button" className="btn btn-outline-success" onClick={() => { setModalOpen(true); setEditing(null); }}><FaPlus className="me-1"/> Add</button>
        </div>
      </form>

      <SubMasterModal 
        show={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onSave={handleModalSave} 
        initial={editing || { title }} 
        relatedOptions={supportsRelations ? relatedItems : []}
        relatedLabel={relatedLabel || ''}
        showRelatedField={supportsRelations}
      />

      <div>
        {loading ? (
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Name</th>
                  {supportsRelations && <th>{relatedLabel}</th>}
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={supportsRelations ? 4 : 3} className="text-center">No items found</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{it.name}</td>
                    {supportsRelations && <td>{renderRelatedTags(it)}</td>}
                    <td>{it.active ? 'Yes' : 'No'}</td>
                    <td>
                      <div className="btn-group" role="group">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleToggle(it.id, !!it.active)}>
                          {it.active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(it)} title="Edit"><FaEdit/></button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(it.id)} title="Delete"><FaTrash/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubMasterManager;
